import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { Row, Col } from 'react-bootstrap';
import { IconButton, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { MessageModal } from 'components/modal/MessageModal';
import { useRestApi } from 'context/RestApiContext';
import { checkRequired } from 'utils/validationUtils';
import TextComponent from 'components/Input/TextComponent';
import QRCode from 'react-qr-code';
import { isPastDate } from 'utils/dateUtils';
import ExpiredMessageMasterModal from './ExpiredMessageMasterModal';
import './ClientCard.css'; // 追加: 説明のスタイルを流用

const INDEFINITE_EXPIRE_DATE = '9999-12-31';

function normalizeExpireForInput(expireValue) {
  if (!expireValue) return '';

  // サーバから '9999-12-31' / '9999/12/31' / 時刻付きで返る可能性があるため、まず日付部分だけに正規化
  const normalized = String(expireValue).trim().replaceAll('/', '-').slice(0, 10);
  if (normalized === INDEFINITE_EXPIRE_DATE) return '';

  return normalized;
}

// 打合せ履歴お客様共有QRコードモーダルダイアログ
function MeetingHistoryQrCodeModal(props) {
  const restApi = useRestApi();

  const [show, setShow] = useState(false);
  const [clientCd, setClientCd] = useState('');
  // エラーフィールド
  const [errorFileds, setErrorFileds] = useState([]);
  // メッセージモーダル
  const [dialog, setDialog] = useState({});
  // 共有URL & 有効期限
  const [shareUrl, setShareUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const qrRef = useRef(null);
  const [freeText, setFreeText] = useState('');
  // フリーテキスト編集状態
  const [isEditingFreeText, setIsEditingFreeText] = useState(false);
  const [editedFreeText, setEditedFreeText] = useState('');
  // 期限切れメッセージ関連
  const [expiredMessage, setExpiredMessage] = useState('');
  const [messagePatterns, setMessagePatterns] = useState([]);
  const [selectedMessageOption, setSelectedMessageOption] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [showMasterModal, setShowMasterModal] = useState(false);
  const [isEditingExpiredMessage, setIsEditingExpiredMessage] = useState(false);

  const CUSTOM_OPTION = '__custom__';

  const handleClose = () => setShow(false);

  useEffect(() => {
    if (props?.params?.show) {
      setClientCd(props.params.clientCd);
      // 共有情報取得
      restApi.get(`/api/shared/info/${props.params.clientCd}`, (data) => {
        const sharedInfo = data?.sharedInfo ?? {};

        setExpiresAt(normalizeExpireForInput(sharedInfo?.EXPIRE));
        setShareUrl(sharedInfo?.URL ?? '');
        setFreeText(sharedInfo?.SHARED_FREE_TEXT ?? '');
        setEditedFreeText(sharedInfo?.SHARED_FREE_TEXT ?? '');
        setIsEditingFreeText(false);
        setExpiredMessage(sharedInfo?.EXPIRED_MESSAGE ?? '');

        /** 有効期限が切れている場合はメッセージを表示 */
        if (sharedInfo?.URL && isPastDate(sharedInfo?.EXPIRE)) {
          setErrorMessage('※有効期限が切れています');
        } else {
          setErrorMessage('');
        }

        setShow(true);
      });
      // メッセージパターン一覧取得
      fetchMessagePatterns();
    }
  }, [props.params]);

  // 共有URLを生成
  const handleClickGenerateShare = () => {
    const expireToSend = expiresAt ? expiresAt : INDEFINITE_EXPIRE_DATE;
    restApi.post(
      '/api/meeting_history/shared_url',
      {
        CLIENT_CD: clientCd,
        URL: shareUrl,
        EXPIRE: expireToSend,
      },
      (data) => {
        setShareUrl(data.url);
        setErrorMessage(isPastDate(expireToSend) ? '※有効期限が切れています' : '');
      }
    );
  };

  // フリーテキスト編集ハンドラ
  const handleClickFreeText = () => setIsEditingFreeText(true);
  const handleChangeFreeText = (e) => setEditedFreeText(e.target.value);
  const handleSaveFreeText = () => {
    restApi.post(
      '/api/meeting_history/shared_free_text',
      {
        CLIENT_CD: clientCd,
        SHARED_FREE_TEXT: editedFreeText,
      },
      (_) => {
        setFreeText(editedFreeText);
        setIsEditingFreeText(false);
      }
    );
  };
  const handleCancelFreeText = () => {
    setEditedFreeText(freeText);
    setIsEditingFreeText(false);
  };

  // メッセージパターン一覧取得
  const fetchMessagePatterns = () => {
    restApi.get('/api/expired_messages', (data) => {
      const patterns = data?.messages ?? [];
      setMessagePatterns(patterns);
    });
  };

  // メッセージパターン取得後にプルダウン初期値を設定
  useEffect(() => {
    if (messagePatterns.length === 0) return;
    if (!expiredMessage) {
      // 未設定の場合は最初のパターンを選択
      setSelectedMessageOption(messagePatterns[0]?.MESSAGE_TEXT ?? '');
      setCustomMessage('');
      return;
    }
    // マスタのパターンと一致するか確認
    const matched = messagePatterns.find((p) => p.MESSAGE_TEXT === expiredMessage);
    if (matched) {
      setSelectedMessageOption(matched.MESSAGE_TEXT);
      setCustomMessage('');
    } else {
      // マスタにないメッセージ → その他
      setSelectedMessageOption(CUSTOM_OPTION);
      setCustomMessage(expiredMessage);
    }
  }, [messagePatterns, expiredMessage]);

  // 期限切れメッセージ保存
  const handleSaveExpiredMessage = () => {
    const messageToSave =
      selectedMessageOption === CUSTOM_OPTION ? customMessage : selectedMessageOption;
    if (!messageToSave.trim()) return;
    restApi.post(
      '/api/meeting_history/shared_expired_message',
      {
        CLIENT_CD: clientCd,
        EXPIRED_MESSAGE: messageToSave,
      },
      (_) => {
        setExpiredMessage(messageToSave);
        setIsEditingExpiredMessage(false);
      }
    );
  };

  // 期限切れメッセージ編集キャンセル
  const handleCancelExpiredMessage = () => {
    // プルダウンの選択状態を元に戻す
    if (expiredMessage) {
      const matched = messagePatterns.find((p) => p.MESSAGE_TEXT === expiredMessage);
      if (matched) {
        setSelectedMessageOption(matched.MESSAGE_TEXT);
        setCustomMessage('');
      } else {
        setSelectedMessageOption(CUSTOM_OPTION);
        setCustomMessage(expiredMessage);
      }
    }
    setIsEditingExpiredMessage(false);
  };

  // マスタ更新後のコールバック
  const handleMasterUpdated = () => {
    fetchMessagePatterns();
  };

  // PNGダウンロード処理
  const handleDownloadPng = () => {
    if (!qrRef.current) return;
    const svgEl = qrRef.current; // SVG要素
    const serializer = new XMLSerializer();
    let source = serializer.serializeToString(svgEl);

    // XML宣言付与（互換性向上）
    if (!source.match(/^<\?xml/)) {
      source = '<?xml version="1.0" standalone="no"?>\n' + source;
    }

    const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    const size = 192; // QR表示サイズと合わせる

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      // 背景白
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      const pngUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = 'QRコード.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };
    img.onerror = () => {
      console.error('QRコード画像の生成に失敗しました');
    };
    img.src = url;
  };

  return (
    <Dialog fullWidth maxWidth="md" aria-labelledby="customized-dialog-title" open={show}>
      <DialogTitle>打合せ履歴の共有</DialogTitle>
      <DialogContent>
        <div className="mb-3">
          <label className="client-card-label">有効期限</label>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ width: 200 }}>
              <TextComponent
                value={expiresAt}
                onChange={(target) => {
                  setExpiresAt(target.value);
                  setErrorFileds([...errorFileds].filter((x) => x != 'expiresAt'));
                }}
                type="date"
              />
            </div>
            {shareUrl ? (
              <Button variant="primary" className="btn-fill" onClick={handleClickGenerateShare}>
                有効期限を更新
              </Button>
            ) : (
              <Button variant="success" className="btn-fill" onClick={handleClickGenerateShare}>
                共有URL生成
              </Button>
            )}
          </div>
          {!shareUrl && (
            <small className="text-muted">指定した日までアクセス可能なURLを発行します。</small>
          )}
          <div>
            <small className="text-muted">※空欄の場合は無期限として扱います。</small>
          </div>
          {errorMessage && (
            <div className="mt-2">
              <small style={{ color: 'red' }}>{errorMessage}</small>
            </div>
          )}
        </div>

        {shareUrl && (
          <div className="mt-3">
            <label className="client-card-label">URL</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                readOnly
                className="form-control"
                value={shareUrl}
                style={{ flex: 1, minWidth: 280 }}
              />
              <Button
                variant="secondary"
                title="コピー"
                onClick={() => navigator.clipboard?.writeText(shareUrl)}
              >
                コピー
              </Button>
              <Button
                as="a"
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="primary"
                title="リンクを別タブで開きます"
              >
                ページを確認
              </Button>
            </div>
            <div
              className="mt-3"
              style={{
                background: 'white',
                padding: 12,
                display: 'inline-block',
                cursor: 'pointer',
                userSelect: 'none',
                border: '1px solid #eee',
                borderRadius: 4,
              }}
              onClick={handleDownloadPng}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDownloadPng();
                }
              }}
              role="button"
              tabIndex={0}
              title="クリックしてPNGダウンロード"
              aria-label="QRコード（クリックでPNGダウンロード）"
            >
              <QRCode
                ref={qrRef}
                value={shareUrl}
                size={192}
                aria-label="打合せ履歴共有QRコード (クリックでPNG保存)"
              />
            </div>
            <div className="mt-2">
              <small className="text-muted">QRコードをクリックするとPNGで保存します。</small>
            </div>

            {/* フリーテキスト: クリックで編集”UI */}
            <div className="mt-3">
              <label className="client-card-label">フリーテキスト</label>
              {isEditingFreeText ? (
                <>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={editedFreeText}
                    onChange={handleChangeFreeText}
                    placeholder="お客様に共有したいメモや補足を入力してください"
                    autoFocus
                  />
                  <div className="mt-2">
                    <Button
                      className="btn-fill"
                      variant="primary"
                      size="sm"
                      onClick={handleSaveFreeText}
                    >
                      保存
                    </Button>
                    <Button
                      className="btn-fill ml-2"
                      variant="secondary"
                      size="sm"
                      onClick={handleCancelFreeText}
                    >
                      キャンセル
                    </Button>
                  </div>
                </>
              ) : (
                <span
                  className="editable-description"
                  onClick={handleClickFreeText}
                  style={{ color: '#888888' }}
                >
                  {freeText || 'フリーテキストを入力するにはクリックしてください'}
                </span>
              )}
            </div>

            {/* 期限切れメッセージ */}
            <div className="mt-3">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label className="client-card-label" style={{ marginBottom: 0 }}>期限切れメッセージ</label>
                <small>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowMasterModal(true);
                    }}
                    style={{ color: '#888' }}
                  >
                    ⚙️ 期限切れメッセージ管理
                  </a>
                </small>
              </div>
              {isEditingExpiredMessage ? (
                <>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <select
                      className="form-control"
                      style={{ flex: 1, minWidth: 280 }}
                      value={selectedMessageOption}
                      onChange={(e) => {
                        setSelectedMessageOption(e.target.value);
                        if (e.target.value !== CUSTOM_OPTION) {
                          setCustomMessage('');
                        }
                      }}
                    >
                      {messagePatterns.map((p) => (
                        <option key={p.MESSAGE_ID} value={p.MESSAGE_TEXT}>
                          {p.MESSAGE_TEXT}
                        </option>
                      ))}
                      <option value={CUSTOM_OPTION}>その他（手入力）</option>
                    </select>
                  </div>
                  {selectedMessageOption === CUSTOM_OPTION && (
                    <textarea
                      className="form-control mt-2"
                      rows={3}
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="期限切れ時に表示するメッセージを入力してください"
                    />
                  )}
                  <div className="mt-2">
                    <Button
                      className="btn-fill"
                      variant="primary"
                      size="sm"
                      onClick={handleSaveExpiredMessage}
                      disabled={
                        selectedMessageOption === CUSTOM_OPTION && !customMessage.trim()
                      }
                    >
                      保存
                    </Button>
                    <Button
                      className="btn-fill ml-2"
                      variant="secondary"
                      size="sm"
                      onClick={handleCancelExpiredMessage}
                    >
                      キャンセル
                    </Button>
                  </div>
                </>
              ) : (
                <span
                  className="editable-description"
                  onClick={() => setIsEditingExpiredMessage(true)}
                  style={{ color: '#888888', cursor: 'pointer' }}
                >
                   {expiredMessage || '期限切れメッセージを設定するにはクリックしてください'}
                </span>
              )}
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions className="p-3">
        <Button variant="secondary" className="footer-button" onClick={handleClose}>
          閉じる
        </Button>
      </DialogActions>
      {/** メッセージモーダル */}
      <MessageModal dialog={dialog} />
      {/** マスタ管理モーダル */}
      <ExpiredMessageMasterModal
        show={showMasterModal}
        onClose={() => setShowMasterModal(false)}
        onUpdated={handleMasterUpdated}
      />
    </Dialog>
  );
}
export default MeetingHistoryQrCodeModal;
